import React, { useEffect, useMemo, useState } from 'react';
import { Select } from '@/components/ui/select';
import useSWR from 'swr';
import { ClipboardPaste, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SalesPlanCard } from '@/pages/Usage/WorkerDashboardPage';
import { MonthPlanGrid } from '@/components/salesPlan/MonthPlanGrid';
import { SalesPlanSheetSync } from '@/components/salesPlan/SalesPlanSheetSync';
import { Textarea } from '@/components/ui/textarea';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useAppState } from '../app/appContext';
import { useCan } from '@/hooks/useCan';
import { refreshZoneFeatures } from '@/hooks/useZoneFeatures';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import {
  getSalesPlan,
  getSalesPlanToday,
  putSalesPlan,
  setSalesPlanOnDashboard,
  salesPlanKey,
  type SalesPlanItemDto,
  type SalesPlanKind,
} from '@/api/salesPlan';

// «План продаж» (Алексей 22.10, #1941/#1942). Без чеков — список на смену: что предлагать
// в какое время дня («Сырники» до 12:00, днём хот-доги), напоминания («Керамика») и цели дня
// «позиция → количество». Расписание строки: дни недели или конкретная дата + окно времени.
// Таблицу можно вставить из Excel («Вставить списком»). Сохраняется план целиком.

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const SECTIONS: { kind: SalesPlanKind; title: string; description: string; placeholder: string; pasteHint: string }[] = [
  {
    kind: 'offer',
    title: 'Что предлагаем',
    description: 'Крупно на дашборде смены в своё время дня.',
    placeholder: 'Сырники',
    pasteHint: 'Строка — позиция и, через Tab, время: «Сырники⇥до 12:00», «Хот-доги⇥12:00–17:00»',
  },
  {
    kind: 'reminder',
    title: 'Напоминания',
    description: 'Короткие подсказки рядом: «Керамика», «Карта лояльности».',
    placeholder: 'Керамика',
    pasteHint: 'Строка — текст напоминания, можно через Tab время: «Керамика», «Десерт к кофе⇥с 15:00»',
  },
];

type Row = SalesPlanItemDto & { key: string };
let seq = 0;
const newKey = () => `n${++seq}`;
const hhmm = (v: string | null) => (v ? v.slice(0, 5) : '');

export default function SalesPlanSettings() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  if (!zoneId) return <BranchRequiredState />;
  return <SalesPlanBody zoneId={zoneId} />;
}

/** «до 12:00» / «12:00–17:00» / «с 15:00» / «12-17» → [from, to]. */
function parseWindow(s: string): [string | null, string | null] {
  const t = s.trim().toLowerCase().replace(/\s+/g, ' ');
  const norm = (x: string) => {
    const m = x.match(/(\d{1,2})(?:[:.](\d{2}))?/);
    if (!m) return null;
    const h = Math.min(23, Number(m[1]));
    return `${String(h).padStart(2, '0')}:${m[2] ?? '00'}`;
  };
  if (!t) return [null, null];
  if (t.startsWith('до')) return [null, norm(t)];
  if (t.startsWith('с') && !/[–—-]/.test(t)) return [norm(t), null];
  const parts = t.split(/[–—-]/);
  if (parts.length === 2) return [norm(parts[0]), norm(parts[1])];
  return [null, null];
}

function SalesPlanBody({ zoneId }: { zoneId: string }) {
  const canEdit = useCan('zone.config.edit', zoneId);
  const { data, isLoading, mutate } = useSWR(salesPlanKey(zoneId), () => getSalesPlan(zoneId).then((r) => r.data));
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { data: today, mutate: mutateToday } = useSWR(['sales-plan-today', zoneId], () => getSalesPlanToday(zoneId).then((r) => r.data));
  const [dashBusy, setDashBusy] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  async function toggleDashboard(on: boolean) {
    setDashBusy(true);
    setError(null);
    try {
      await setSalesPlanOnDashboard(zoneId, on);
      await mutate();
      refreshZoneFeatures(zoneId);
    } catch (e) {
      setError(extractApiError(e, 'Не удалось переключить'));
    } finally {
      setDashBusy(false);
    }
  }

  useEffect(() => {
    if (data) setRows(data.items.map((it) => ({ ...it, key: `s${it.id}` })));
  }, [data]);

  const dirty = useMemo(() => {
    if (!data || !rows) return false;
    const strip = (r: SalesPlanItemDto) => JSON.stringify([r.kind, r.title, hhmm(r.time_from), hhmm(r.time_to), r.weekdays_mask, r.on_date, r.target_qty]);
    return JSON.stringify(rows.map(strip)) !== JSON.stringify(data.items.map(strip));
  }, [data, rows]);

  if (isLoading || !rows) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загружаем…
      </div>
    );
  }
  const disabled = !canEdit || busy;
  const update = (key: string, patch: Partial<Row>) => {
    setSaved(false);
    setRows((rs) => (rs ?? []).map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const remove = (key: string) => { setSaved(false); setRows((rs) => (rs ?? []).filter((r) => r.key !== key)); };
  const add = (kind: SalesPlanKind, extra: Partial<Row>[] = [{}]) => {
    setSaved(false);
    setRows((rs) => [
      ...(rs ?? []),
      ...extra.map((e) => ({ key: newKey(), kind, title: '', time_from: null, time_to: null, weekdays_mask: 127, on_date: null, target_qty: kind === 'target' ? 10 : null, ...e })),
    ]);
  };

  async function save() {
    if (!rows) return;
    setBusy(true);
    setError(null);
    try {
      const items = rows
        .filter((r) => r.title.trim())
        .map(({ key: _k, id: _id, ...r }) => ({ ...r, time_from: r.time_from || null, time_to: r.time_to || null, on_date: r.on_date || null }));
      await putSalesPlan(zoneId, items);
      await mutate();
      void mutateToday();
      refreshZoneFeatures(zoneId);
      setSaved(true);
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить план'));
    } finally {
      setBusy(false);
    }
  }

  const secondary = rows.filter((r) => r.kind !== 'target').length;
  return (
    <div className="space-y-4">
      {/* Главное — план на месяц по дням таблицей как в Excel (Алексей 27.10 #2986/#2987). */}
      <Card variant="section">
        <CardHeader>
          <CardTitle>План продаж на месяц</CardTitle>
          <CardDescription>Сколько каждой позиции продать по дням. Сотрудник видит цель дня на дашборде смены.</CardDescription>
        </CardHeader>
        <CardContent>
          <MonthPlanGrid rows={rows} disabled={disabled} newKey={newKey}
            onChange={(fn) => { setSaved(false); setRows((rs) => fn(rs ?? [])); }} />
          <div className="mt-4 border-t pt-3">
            <div className="mb-1.5 text-sm font-medium">Из Google-таблицы</div>
            <SalesPlanSheetSync zoneId={zoneId} disabled={!canEdit} onSynced={() => { void mutate(); void mutateToday(); }} />
          </div>
        </CardContent>
      </Card>
      {/* Тоггл «выводить на дашборд смены» + как будет выглядеть виджет (Алексей 27.10 #2962). */}
      <Card variant="section">
        <CardContent className="space-y-3 pt-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label htmlFor="sales-plan-dashboard" className="text-sm font-medium">Показывать на дашборде смены</Label>
              <p className="text-xs text-muted-foreground">Выключено — план можно заполнить и отсмотреть, сотрудники его не видят.</p>
            </div>
            <div className="flex items-center gap-2">
              {dashBusy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
              <Switch
                id="sales-plan-dashboard"
                checked={!!data?.show_on_dashboard}
                disabled={!canEdit || dashBusy}
                onCheckedChange={toggleDashboard}
              />
            </div>
          </div>
          {today && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">Так виджет выглядит на дашборде сейчас ({today.now}){dirty ? ' — после сохранения обновится' : ''}</div>
              <div className="max-w-md rounded-xl border bg-card p-3 shadow-sm">
                <SalesPlanCard data={today} bare />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <button type="button" onClick={() => setMoreOpen((v) => !v)}
        className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
        {moreOpen ? 'Скрыть' : 'Ещё'}: что предлагаем в течение дня и напоминания{secondary ? ` (${secondary})` : ''}
      </button>
      {moreOpen && SECTIONS.map((sec) => (
        <PlanSection
          key={sec.kind}
          section={sec}
          rows={rows.filter((r) => r.kind === sec.kind)}
          disabled={disabled}
          onUpdate={update}
          onRemove={remove}
          onAdd={(extra) => add(sec.kind, extra)}
        />
      ))}
      <div className="sticky bottom-3 z-10 flex items-center gap-3">
        <Button onClick={save} disabled={disabled || !dirty}>
          {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
          Сохранить план
        </Button>
        {saved && !dirty && <span className="text-sm text-muted-foreground">Сохранено</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </div>
  );
}

function PlanSection({ section, rows, disabled, onUpdate, onRemove, onAdd }: {
  section: (typeof SECTIONS)[number];
  rows: Row[];
  disabled: boolean;
  onUpdate: (key: string, patch: Partial<Row>) => void;
  onRemove: (key: string) => void;
  onAdd: (extra?: Partial<Row>[]) => void;
}) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const isTarget = section.kind === 'target';

  function applyPaste() {
    const parsed: Partial<Row>[] = [];
    for (const line of pasteText.split(/\r?\n/)) {
      const cells = line.split(/\t|;/).map((c) => c.trim()).filter(Boolean);
      if (!cells.length) continue;
      const [title, second] = cells;
      if (isTarget) {
        const qty = Number(String(second ?? '').replace(/\s/g, '').replace(',', '.'));
        if (!Number.isFinite(qty) || qty <= 0) continue; // шапка таблицы и пустые строки
        parsed.push({ title, target_qty: Math.round(qty) });
      } else {
        const [from, to] = second ? parseWindow(second) : [null, null];
        parsed.push({ title, time_from: from, time_to: to });
      }
    }
    if (parsed.length) onAdd(parsed);
    setPasteText('');
    setPasteOpen(false);
  }

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{section.title}</CardTitle>
        <CardDescription>{section.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Пока пусто.</p>}
        {rows.map((r) => (
          <PlanRow key={r.key} row={r} isTarget={isTarget} placeholder={section.placeholder} disabled={disabled}
            onChange={(p) => onUpdate(r.key, p)} onRemove={() => onRemove(r.key)} />
        ))}
        {pasteOpen && (
          <div className="space-y-2 rounded-lg border border-dashed p-3">
            <p className="text-xs text-muted-foreground">{section.pasteHint}</p>
            <Textarea rows={5} value={pasteText} onChange={(e) => setPasteText(e.target.value)} autoFocus
              placeholder={isTarget ? 'Сырники\t20\nХот-доги\t15' : 'Сырники\tдо 12:00\nХот-доги\t12:00–17:00'} />
            <div className="flex gap-2">
              <Button size="sm" onClick={applyPaste} disabled={!pasteText.trim()}>Добавить строки</Button>
              <Button size="sm" variant="ghost" onClick={() => { setPasteOpen(false); setPasteText(''); }}>Отмена</Button>
            </div>
          </div>
        )}
        {!disabled && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => onAdd()}>
              <Plus className="mr-1 size-4" /> Добавить
            </Button>
            {!pasteOpen && (
              <Button size="sm" variant="ghost" onClick={() => setPasteOpen(true)}>
                <ClipboardPaste className="mr-1 size-4" /> Вставить списком
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Простая строка (Алексей 22.10, #1969 «настройки слишком сложные»): что + когда (готовые
// варианты, своё время — по выбору) + дни одной кнопкой-сводкой, которая раскрывает дни/дату.
const WHEN: { key: string; label: string; from: string | null; to: string | null }[] = [
  { key: 'all', label: 'Весь день', from: null, to: null },
  { key: 'morning', label: 'Утро · до 12:00', from: null, to: '12:00' },
  { key: 'day', label: 'День · 12–17', from: '12:00', to: '17:00' },
  { key: 'evening', label: 'Вечер · с 17:00', from: '17:00', to: null },
];

function whenKey(r: Row): string {
  const f = hhmm(r.time_from) || null, t = hhmm(r.time_to) || null;
  return WHEN.find((w) => w.from === f && w.to === t)?.key ?? 'custom';
}

function daysLabel(r: Row): string {
  if (r.on_date) return `Только ${r.on_date.split('-').reverse().slice(0, 2).join('.')}`;
  const m = r.weekdays_mask;
  if (m === 127) return 'Каждый день';
  if (m === 31) return 'Пн–Пт';
  if (m === 96) return 'Сб, Вс';
  return DAYS.filter((_, i) => m & (1 << i)).join(', ');
}

function PlanRow({ row, isTarget, placeholder, disabled, onChange, onRemove }: {
  row: Row;
  isTarget: boolean;
  placeholder: string;
  disabled: boolean;
  onChange: (p: Partial<Row>) => void;
  onRemove: () => void;
}) {
  const [daysOpen, setDaysOpen] = useState(false);
  const wk = whenKey(row);
  const [custom, setCustom] = useState(wk === 'custom');
  const dated = !!row.on_date;
  return (
    <div className="rounded-lg border bg-card px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-9 min-w-40 flex-1" value={row.title} placeholder={placeholder} disabled={disabled}
          onChange={(e) => onChange({ title: e.target.value })} />
        {isTarget ? (
          <div className="flex items-center gap-1">
            <Input className="h-9 w-20 text-right tabular-nums" type="number" min={1} value={row.target_qty ?? ''} disabled={disabled}
              onChange={(e) => onChange({ target_qty: e.target.value ? Math.max(1, Number(e.target.value)) : null })} />
            <span className="text-xs text-muted-foreground">шт</span>
          </div>
        ) : (
          <Select wrapperClassName="w-auto" className="pl-2" disabled={disabled}
            value={custom ? 'custom' : wk}
            onChange={(e) => {
              const w = WHEN.find((x) => x.key === e.target.value);
              if (w) { setCustom(false); onChange({ time_from: w.from, time_to: w.to }); } else setCustom(true);
            }}>
            {WHEN.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
            <option value="custom">Своё время…</option>
          </Select>
        )}
        {!isTarget && custom && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            с
            <Input className="h-9 w-[5.5rem]" type="time" value={hhmm(row.time_from)} disabled={disabled}
              onChange={(e) => onChange({ time_from: e.target.value || null })} />
            до
            <Input className="h-9 w-[5.5rem]" type="time" value={hhmm(row.time_to)} disabled={disabled}
              onChange={(e) => onChange({ time_to: e.target.value || null })} />
          </div>
        )}
        <button type="button" onClick={() => setDaysOpen((v) => !v)} disabled={disabled}
          className={cn('h-9 rounded-md border px-2.5 text-sm', daysOpen && 'border-primary text-primary')}>
          {daysLabel(row)}
        </button>
        {!disabled && (
          <Button size="icon" variant="ghost" className="size-9 shrink-0" onClick={onRemove} aria-label="Удалить">
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
      {daysOpen && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
          <div className={cn('flex items-center gap-0.5', dated && 'opacity-40')}>
            {DAYS.map((d, i) => {
              const on = !!(row.weekdays_mask & (1 << i));
              return (
                <button key={d} type="button" disabled={disabled || dated}
                  onClick={() => { const m = row.weekdays_mask ^ (1 << i); if (m) onChange({ weekdays_mask: m }); }}
                  className={cn('h-8 w-8 rounded-md text-xs font-medium transition-colors',
                    on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70')}>
                  {d}
                </button>
              );
            })}
          </div>
          <span className="text-xs text-muted-foreground">или только в день</span>
          <Input className="h-8 w-[9.5rem]" type="date" value={row.on_date ?? ''} disabled={disabled}
            onChange={(e) => onChange({ on_date: e.target.value || null })} />
        </div>
      )}
    </div>
  );
}
